import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { WebService } from './web.service';

// =====================================================================
// SwapDataService — Scope 6.2 Swap Management
// ---------------------------------------------------------------------
// Thin wrapper over routes/swaps.js (mounted at /api/v1/swaps).
// Same pattern as OutboundDataService:
//   - WebService.getWebCall picks GET vs POST by whether `body` is passed
//   - every write sends ActionByGUID from localStorage('GUID')
//
// Swap types (dbo.swap_type):
//   1 Reserve → Reserve   bags move into the receiving customer's reserve
//   2 Reserve → Order     bags move onto a new Draft order
//   3 Release             bags go back to general population (no receiver)
// =====================================================================

/** dbo.swap_type: 1 Reserve → Reserve · 2 Reserve → Order · 3 Release to general population */
export type SwapTypeID = 1 | 2 | 3;

/** Body for POST /requests/save (save_swap_request) */
export interface SwapRequestPayload {
  SwapRequestID?: number | null;              // null/omitted = create new
  SwapTypeID: SwapTypeID;                     // proc defaults to 2 if missing
  RequestingCompanyID: number | null;         // null for Release (type 3)
  RequestedInventoryTypeID: number | null;    // Release: proc takes the coffee from the source line
  BagsRequested: number;                      // Release: same as ProposedBags
  DestinationWarehouseID?: number | null;     // null for Release
  CrossWarehouseAllowed?: boolean;
  TargetDate?: string | null;                 // 'YYYY-MM-DD'
  Priority?: string;
  Reason?: string | null;
  SourceReserveOrderID?: number | null;       // required for Release (50085)
  SourceInventoryID?: number | null;
  MatchType?: string | null;                  // 'Exact' / 'Alternative' / 'Release'
  MatchScore?: number | null;                 // null for Release
  ProposedBags?: number | null;
}

/** Query for GET /candidates (get_swap_candidates) — types 1 and 2 only */
export interface SwapCandidateQuery {
  RequestingCompanyID: number;
  InventoryTypeID: number;
  BagsNeeded: number;
  WarehouseID?: number | null;
  CrossWarehouseAllowed?: boolean;
  IncludeAlternatives?: boolean;
  MinAlternativeScore?: number;
  TopN?: number;
  ExcludeSwapRequestID?: number | null;       // don't count this swap's own held bags
}

export type SwapDecision = 'Approve' | 'ApproveRevised' | 'Reject' | 'Return';

@Injectable({
  providedIn: 'root'
})
export class SwapDataService {
  constructor(private web: WebService) { }

  /** Logged-in user's GUID (set at login in auth.service.ts) */
  get currentUserGUID(): string | null {
    return localStorage.getItem('GUID');
  }

  // Drops null/undefined/'' so the query string only carries real values.
  // Booleans go as 1/0 because the route reads them as numbers.
  private toParams(obj: Record<string, any>): { name: string, value: any }[] {
    return Object.entries(obj)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([name, value]) => ({ name, value: typeof value === 'boolean' ? (value ? 1 : 0) : value }));
  }

  // ---------------------------------------------------------------------
  // Lookups
  // ---------------------------------------------------------------------

  /** Desired-coffee picker — coffees with open reserve bags come first */
  getCoffeeOptions(): Observable<any[]> {
    return this.web.getWebCall('swaps', 'coffee-options');
  }

  /** Ranked candidate reserve lines + summary (6.2.3, 6.2.6–6.2.16) — types 1 and 2 */
  getCandidates(q: SwapCandidateQuery): Observable<{ Candidates: any[]; Summary: any }> {
    return this.web.getWebCall('swaps', 'candidates', { params: this.toParams(q) });
  }

  /**
   * One customer's own open reserve lines — the source picker for Release (type 3).
   * Rows have the same shape as candidates (ReserveOrderID, InventoryID,
   * AvailableToSwap, Price, ApprovingTraderName, MatchType = 'Release' ...)
   * so the detail screen can reuse selectCandidate().
   */
  getReserveLines(companyId: number, inventoryTypeId?: number | null, excludeSwapRequestId?: number | null): Observable<any[]> {
    return this.web.getWebCall('swaps', 'reserve-lines', {
      params: this.toParams({
        CompanyID: companyId,
        InventoryTypeID: inventoryTypeId,
        ExcludeSwapRequestID: excludeSwapRequestId
      })
    });
  }

  // ---------------------------------------------------------------------
  // List / detail
  // ---------------------------------------------------------------------

  /** List screen + status counts (6.2.1). Rows include SwapTypeName for the Type column */
  getRequests(opts?: { SwapStatusID?: number | null; Search?: string; MineOnly?: boolean }): Observable<{ Requests: any[]; Counts: any }> {
    const params = this.toParams({
      SwapStatusID: opts?.SwapStatusID,
      Search: opts?.Search,
      MineOnly: opts?.MineOnly,
      UserGUID: opts?.MineOnly ? this.currentUserGUID : null
    });
    return this.web.getWebCall('swaps', 'requests', params.length ? { params } : undefined);
  }

  /** Detail pulldown: { Swap, History, Notes } */
  getRequestDetail(swapRequestId: number): Observable<{ Swap: any; History: any[]; Notes: any[] }> {
    return this.web.getWebCall('swaps', `requests/${swapRequestId}`);
  }

  /** Before/after + validation results (6.2.24–6.2.30, 6.2.41–6.2.45). Includes TargetDescription per type */
  getExecutionPreview(swapRequestId: number): Observable<any> {
    return this.web.getWebCall('swaps', `requests/${swapRequestId}/preview`);
  }

  // ---------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------

  /** Create (no SwapRequestID) or edit a Draft/Returned swap. Returns { SwapRequestID } */
  saveRequest(payload: SwapRequestPayload): Observable<{ success: boolean; SwapRequestID: number }> {
    return this.web.getWebCall('swaps', 'requests/save', {
      body: { ...payload, ActionByGUID: this.currentUserGUID }
    });
  }

  /** Send to the source reserve's trader for approval (6.2.18) */
  submitRequest(swapRequestId: number): Observable<any> {
    return this.web.getWebCall('swaps', 'requests/submit', {
      body: { SwapRequestID: swapRequestId, ActionByGUID: this.currentUserGUID }
    });
  }

  /** Approve / Approve revised qty / Reject / Return (6.2.19–6.2.22) */
  decideRequest(swapRequestId: number, decision: SwapDecision, comments: string,
    approvedBags?: number | null, approvedPrice?: number | null): Observable<any> {
    return this.web.getWebCall('swaps', 'requests/decide', {
      body: {
        SwapRequestID: swapRequestId,
        Decision: decision,
        ApprovedBags: approvedBags ?? null,
        ApprovedPrice: approvedPrice ?? null,
        Comments: comments,
        ActionByGUID: this.currentUserGUID
      }
    });
  }

  /**
   * Execute (6.2.31). What comes back depends on the type:
   *   1 → TargetReserveOrderID (+ TargetReserveCreated = new reserve or existing)
   *   2 → OrderID of the new Draft order
   *   3 → neither (bags just leave the source reserve)
   */
  executeSwap(swapRequestId: number): Observable<{
    success: boolean;
    SwapTypeID: SwapTypeID;
    SwapNumber: string;
    OrderID: number | null;
    TargetReserveOrderID: number | null;
    TargetReserveCreated: boolean | null;
    RequesterNotified?: boolean;
  }> {
    return this.web.getWebCall('swaps', 'requests/execute', {
      body: { SwapRequestID: swapRequestId, ActionByGUID: this.currentUserGUID }
    });
  }

  /** Cancel a Draft / Pending / Returned / Approved swap */
  cancelRequest(swapRequestId: number, reason: string | null): Observable<any> {
    return this.web.getWebCall('swaps', 'requests/cancel', {
      body: { SwapRequestID: swapRequestId, Reason: reason, ActionByGUID: this.currentUserGUID }
    });
  }

  // ---------------------------------------------------------------------
  // Notes (6.2.46) + audit (6.2.48)
  // ---------------------------------------------------------------------

  saveNote(note: {
    SwapNoteID?: number | null;
    SwapRequestID: number;
    NoteType: string;
    IsInternal: boolean;
    RelatedStep?: string | null;
    FollowUpRequired: boolean;
    FollowUpDate?: string | null;
    NoteText: string;
  }): Observable<{ success: boolean; SwapNoteID: number }> {
    return this.web.getWebCall('swaps', 'notes/save', {
      body: { ...note, ActionByGUID: this.currentUserGUID }
    });
  }

  /** Audit rows for CSV export — whole report (list) or one swap (detail) */
  getAuditReport(filters?: { FromDate?: string; ToDate?: string; SwapRequestID?: number; ActionType?: string; EntityType?: string; Search?: string }): Observable<any[]> {
    const params = this.toParams(filters ?? {});
    return this.web.getWebCall('swaps', 'audit', params.length ? { params } : undefined);
  }
}

// Tiny CSV download helper — used by the list (full audit report) and the
// detail's Audit tab (one swap). Lives here to avoid a list<->detail import loop.
export function downloadCsv(rows: any[], fileName: string) {
  if (!rows.length) {
    window.alert('Nothing to export.');
    return;
  }
  const headers = Object.keys(rows[0]);

  const escape = (v: any) => {
    let s = v === null || v === undefined ? '' : String(v);
    // SQL datetimes arrive as '2026-09-24T11:02:13.870Z' — that's already
    // Eastern wall-clock time (the API labels it UTC), so just tidy it to
    // '2026-09-24 11:02' (or '2026-09-24' for date-only) instead of shifting it.
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) {
      s = s.endsWith('T00:00:00.000Z') ? s.substring(0, 10) : s.substring(0, 16).replace('T', ' ');
    }
    // Quote anything with a comma, quote or newline
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const csv = [headers.join(','), ...rows.map(r => headers.map(h => escape(r[h])).join(','))].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}
